import stringify from 'json-stable-stringify';
import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import {StorableSummary} from 'constants/SampleConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import {BioentitySummary, Tag} from 'folder-items/FileConstants';
import InventoryFileStore from 'inventory/InventoryFileStore';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  CustomServerResolutionResult,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, TableColumnType, TypedValue} from 'notebook/tables/TableConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {Schema} from 'schemas/SchemaConstants';
import {findSchemaTag} from 'schemas/tags/TagUtil';
import APIUtil from 'util/APIUtil';

import {ColumnGroup} from './columns/ColumnDefinitions';
import {isTagValueStructuredTableColumn} from './columns/TypePredicates';
import {
  PrimarySample,
  SampleLookupInfo,
  StructuredTableAdapter,
  StructuredTableColumn,
  StructuredTableRowUpdater,
} from './StructuredTableConstants';

/**
 * Represents the latest known ("live") value for a table cell.
 *
 * If a cell represents a field or other property of a sample,
 * and the sample is one of the table's "primary samples",
 * there may be a corresponding sample in the store.
 *
 * Case 1: A typed value
 * =====================
 *
 * If such a model is available, we can extract a (nullable) typed value,
 * the "live value".
 *
 * Case 2: A null typed value
 * ==========================
 *
 * If a cell has a primary sample, but the field or property has no value,
 * the live value may be null.
 *
 * Case 3: No sample
 * =================
 *
 * A cell might not have a primary sample available. This case is represented using
 * the special string 'NO_VALUE'.
 *
 * Note that we can't use `null`, because `null` is used for null typed values (Case 2).
 *
 * Some reasons a cell might have no primary sample available:
 * - The cell represents a property of a sample, but the sample isn't a primary sample
 * - The cell represents a property of a primary sample, but we don't yet support
 *   gathering the live value of the property
 * - The cell's primary sample isn't loaded
 *
 * NO_VALUE vs NULL
 * ================
 *
 * Just to clarify: we return NO_VALUE to prevent a stale indicator from being displayed.
 * We return NULL if the cell is *expected* to be blank.
 */
export type LiveCellValue = TypedValue | null | 'NO_VALUE';

interface StoreDataForLiveCellValues {
  fileSummariesById?: {[bioentityId: string]: BioentitySummary};
  storableSummariesById?: {[storableId: string]: StorableSummary};
  resolutionResultsByResolutionQuery?: {[resolutionQuery: string]: ServerResolutionResult};
}

export function gatherLiveCellValuesForTable<TTableMetadata, TRowMetadata>({
  tableSchemas,
  structuredTableColumns,
  adapter,
  primarySamplesByRow,
  computedTable,
  storeData,
}: {
  tableSchemas: Array<Schema>;
  structuredTableColumns: Array<StructuredTableColumn>;
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  primarySamplesByRow: Array<Array<PrimarySample>>;
  computedTable?: Array<Array<EvaluatedCell>> | null;
  storeData: StoreDataForLiveCellValues;
}): Array<Array<LiveCellValue>> {
  const valuesPerRow = gatherValuesPerRow({
    primarySamplesByRow,
    storeData,
  });

  return valuesPerRow.map((rowValues, rowIndex) =>
    structuredTableColumns.map((column, columnIndex) =>
      getLiveCellValue({
        tableSchemas,
        rowValues,
        column,
        columnIndex,
        primarySamples: primarySamplesByRow[rowIndex],
        evaluatedCell: computedTable?.[rowIndex][columnIndex],
        storeData,
        adapter,
      })
    )
  );
}

type RowValues = Array<{
  columnGroup: ColumnGroup | null;
  schemaId: string | null;
  sampleLookupInfo: SampleLookupInfo | null;
  tags: Array<Tag>;
}>;

function gatherValuesPerRow({
  primarySamplesByRow,
  storeData: {fileSummariesById, storableSummariesById},
}: {
  primarySamplesByRow: Array<Array<PrimarySample>>;
  storeData: {
    fileSummariesById?: {[bioentityId: string]: BioentitySummary};
    storableSummariesById?: {[storableId: string]: StorableSummary};
  };
}): Array<RowValues> {
  const perRowFieldValues: Array<RowValues> = [];
  for (const rowPrimarySamples of primarySamplesByRow) {
    const sampleValues: RowValues = [];

    for (const primarySample of rowPrimarySamples) {
      let tags: Array<Tag> | null = null;
      const {columnGroup, schemaId, sampleLookupInfo} = primarySample;
      if (sampleLookupInfo?.idForLookup != null) {
        const {idForLookup} = sampleLookupInfo;
        if (APIUtil.isBiologicalEntity(idForLookup)) {
          tags = fileSummariesById?.[idForLookup]?.tags ?? null;
        } else if (APIUtil.isStorable(idForLookup)) {
          tags = storableSummariesById?.[idForLookup]?.tags ?? null;
        }
      }
      if (tags) {
        sampleValues.push({
          columnGroup,
          schemaId,
          sampleLookupInfo,
          tags,
        });
      }
    }

    perRowFieldValues.push(sampleValues);
  }

  return perRowFieldValues;
}

function getLiveCellValue<TTableMetadata, TRowMetadata>({
  tableSchemas,
  rowValues,
  column,
  columnIndex,
  primarySamples,
  adapter,
  storeData,
  evaluatedCell,
}: {
  tableSchemas: Array<Schema>;
  rowValues: RowValues;
  column: StructuredTableColumn;
  columnIndex: number;
  primarySamples: Array<PrimarySample>;
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  storeData: StoreDataForLiveCellValues;
  evaluatedCell?: EvaluatedCell;
}): LiveCellValue {
  // Tables can customize how rows should be updated. If the table adapter defines a "row updater",
  // then we check if the row updater modifies the cell in the given column.  If it does, then we return that
  // as our LiveValue as the row updater has "owned" the cell.  If it doesn't return a value, then we
  // use our standard logic to update the LiveValue for the cell.
  const liveCellValue = getLiveCellValueFromRowUpdater({
    column,
    primarySamples,
    adapter,
  });
  if (liveCellValue !== 'NO_VALUE') {
    return liveCellValue;
  }
  if (column.columnType === TableColumnType.DEDICATED_LOOKUP) {
    const resolutions = evaluatedCell?.resolutions;
    if (resolutions != null) {
      for (const resolution of resolutions) {
        const key = stringify(resolution.query);
        const serverResolutionResult = storeData.resolutionResultsByResolutionQuery?.[key] as
          | CustomServerResolutionResult
          | undefined;

        // Don't show a stale indicator if the evaluated cell is still loading or the live value has not
        // loaded into the store yet.
        if (resolution.error?.type === ExcessUtil.LoadingError.type || serverResolutionResult == null) {
          return 'NO_VALUE';
        }

        const resolvedValueFromStore = serverResolutionResult.resolvedValue.typedValue;
        if (!isEqual(resolution.resolvedValue, resolvedValueFromStore)) {
          return resolvedValueFromStore;
        }
      }
    }
  }

  if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
    const idForLookup = rowValues[columnIndex]?.sampleLookupInfo?.idForLookup;
    if (idForLookup != null) {
      const bioentitySummary = storeData.fileSummariesById?.[idForLookup];
      if (bioentitySummary != null) {
        return {
          type: BaseTypedValueType.BIOENTITY,
          bioentitySummary: {id: bioentitySummary.id, name: bioentitySummary.displayLabel},
        };
      }
    }
    // Indicates that we either don't have a sample or don't have live data for the sample, both of which mean
    // that this cell should not have a stale indicator
    return 'NO_VALUE';
  }

  if (column.columnType === TableColumnType.BIOENTITY_NAME) {
    // This will work for registration tables, but not (yet) for tables with >1 primary sample per row
    const idForLookup = primarySamples[0]?.sampleLookupInfo?.idForLookup;
    if (idForLookup != null) {
      assertAndContinue(
        primarySamples.length === 1,
        'LiveCellValues needs to be updated to support rows with multiple primary samples.'
      );
      const bioentitySummary = storeData.fileSummariesById?.[idForLookup];
      if (bioentitySummary != null) {
        return {
          type: BaseTypedValueType.STRING,
          rawValue: bioentitySummary.name,
        };
      }
    }
    return 'NO_VALUE';
  }

  if (column.columnType === TableColumnType.ALIAS) {
    const idForLookup = primarySamples[0]?.sampleLookupInfo?.idForLookup;
    const bioentitySummary = idForLookup ? storeData.fileSummariesById?.[idForLookup] : null;
    const aliasInfos = idForLookup ? InventoryFileStore.getBioentityAliasInfos(idForLookup) : null;
    if (aliasInfos == null || bioentitySummary == null) {
      return 'NO_VALUE';
    }
    return {
      type: BaseTypedValueType.ARRAY,
      rawValue: aliasInfos
        .filter((info) => info.registryCollectionOwner == null && info.alias !== bioentitySummary.name)
        .map((info) => ({type: BaseTypedValueType.STRING, rawValue: info.alias})),
    };
  }

  if (column.columnType === TableColumnType.DNA_BASES) {
    const idForLookup = primarySamples[0]?.sampleLookupInfo?.idForLookup;
    const bioentitySummary: BioentitySummary | SequenceSummary | null = idForLookup
      ? (storeData.fileSummariesById?.[idForLookup] ?? null)
      : null;
    if (bioentitySummary) {
      let bases = '';
      if ((bioentitySummary as SequenceSummary).bases) {
        bases = (bioentitySummary as SequenceSummary).bases ?? '';
      }
      return {
        type: BaseTypedValueType.STRING,
        rawValue: bases,
      };
    }
    return 'NO_VALUE';
  }

  const storablesColumnTypes = [TableColumnType.PLATE, TableColumnType.BOX, TableColumnType.PARENT_STORABLE];
  if (storablesColumnTypes.includes(column.columnType)) {
    // Both Plate Creation Table and Box Creation Table have their storable at index 0 of primarySamples
    let sampleId = primarySamples[0]?.sampleLookupInfo?.idForLookup;

    if (column.columnType === TableColumnType.PARENT_STORABLE) {
      if (sampleId) {
        // For the Location column, return the storable's location
        sampleId = storeData.storableSummariesById?.[sampleId]?.parentStorableId ?? undefined;
      }
    }
    const storableSummary = sampleId != null ? (storeData.storableSummariesById?.[sampleId] ?? null) : null;
    if (storableSummary == null) {
      return 'NO_VALUE';
    }
    return {
      type: BaseTypedValueType.STORABLES,
      storableSummaries: [
        {
          id: storableSummary.id,
          name: storableSummary.name,
        },
      ],
    };
  }

  if (!isTagValueStructuredTableColumn(column) || column.tagSchemaFieldId == null) {
    return 'NO_VALUE';
  }

  const expectedSchemaId = tableSchemas.find((schema) =>
    schema.allFields.some((field) => field.id === column.tagSchemaFieldId)
  )?.id;
  if (
    !assertAndContinue(
      expectedSchemaId != null,
      `Structured table is missing schema for field ID ${column.tagSchemaFieldId}`
    )
  ) {
    return 'NO_VALUE';
  }

  // column.columnGroup may be undefined. We coerce to null to pass a triple-eq check
  const expectedColumnGroup = column.columnGroup ?? null;
  const sampleTags = rowValues.find(
    ({columnGroup, schemaId}) => columnGroup === expectedColumnGroup && schemaId === expectedSchemaId
  )?.tags;
  if (sampleTags == null) {
    return 'NO_VALUE';
  }

  const schemaTag = findSchemaTag(sampleTags, column.tagSchemaFieldId);
  return schemaTag ? convertTagToTypedValue(schemaTag) : null;
}

function getLiveCellValueFromRowUpdater<TTableMetadata, TRowMetadata>({
  column,
  primarySamples,
  adapter,
}: {
  column: StructuredTableColumn;
  primarySamples: Array<PrimarySample>;
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
}): LiveCellValue {
  for (const rowUpdater of adapter.getAllRowUpdaters()) {
    const liveCellValue = getUpdatedTypedValueFromRowUpdater(primarySamples, rowUpdater, column);
    if (liveCellValue !== 'NO_VALUE') {
      return liveCellValue;
    }
  }
  return 'NO_VALUE';
}

function getUpdatedTypedValueFromRowUpdater<TModel, TRowMetadata>(
  primarySamples: Array<PrimarySample>,
  rowUpdater: StructuredTableRowUpdater<TModel, TRowMetadata>,
  column: StructuredTableColumn
): LiveCellValue {
  for (const sample of primarySamples) {
    const {sampleLookupInfo, columnGroup} = sample;
    if (sampleLookupInfo != null) {
      const modelFromCache = rowUpdater.readFromCache(sampleLookupInfo);
      if (modelFromCache != null) {
        const updatedTypeValue = rowUpdater.updateCell(modelFromCache, columnGroup, column);
        if (updatedTypeValue !== 'NO_VALUE') {
          return updatedTypeValue;
        }
      }
    }
  }
  return 'NO_VALUE';
}
